// Sheet I/O — fetches the Google Sheets CSV and talks to the Apps
// Script webhook for card mutations (update/add/suggest). As with
// sync.ts, every webhook request uses `Content-Type: text/plain` to
// skip CORS preflight (Apps Script does not respond to OPTIONS).
//
// The card mutation functions share the webhook credentials with the
// sync controller, so we wrap them in a factory that takes the same
// shape of deps — keeps wiring in bootstrap simple.

import { LANGS, type Lang, type Locale } from "../config/langs";
import type { CardList, FaceSchema } from "../config/lists";
import type { Card } from "../types";

/** Raw CSV text from the published Sheet. Cache-busted per call, with
 *  an abort-based timeout so a hung request surfaces as a retryable
 *  load error instead of leaving the user stranded on the loader (seen
 *  on cold Brave-mobile boots). */
const SHEET_FETCH_TIMEOUT_MS = 10_000;
export async function fetchSheetCsv(url: string): Promise<string> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SHEET_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { cache: "no-store", signal: ctrl.signal });
    if (!res.ok) throw new Error("HTTP " + res.status);
    return await res.text();
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new Error("timeout");
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** Credentials + webhook context needed by the card API ops. */
export type CardApiDeps = {
  getWebhookUrl: () => string;
  getWebhookSecret: () => string;
  getUserName: () => string;
  /** Active deck id. Every webhook call carries it so the Apps Script
   *  can route to the right spreadsheet (same routing contract as the
   *  sync controller). */
  getListId: () => string;
  /** Active deck config — needed to translate face keys to column
   *  names (mono decks use `{face.key}_{locale}` columns, bilingual
   *  decks use Lang-coded columns where face.key IS the column). */
  getList: () => CardList;
  /** Active UI locale — combined with face keys to resolve mono deck
   *  columns. Bilingual decks ignore this (their face.lang is set). */
  getLocale: () => Locale;
  getCards: () => Card[];
  /** Matches syncEnabled — `true` when user/url/secret are all non-empty. */
  enabled: () => boolean;
  /** Localized error messages. `key` is a t() key, but the caller owns
   *  translation so this module stays free of i18n coupling. */
  tr: (key: "add_card_no_sync" | "add_card_need_two" | "add_card_dup") => string;
};

/** Resolve a face to its CSV column name. Bilingual decks store face
 *  keys that ARE Lang codes (= column names); mono decks store slug
 *  keys that need the current locale appended to pick the right column
 *  in the multi-locale sheet. */
function faceColumn(face: FaceSchema, locale: Locale): string {
  return face.lang ? face.key : `${face.key}_${locale}`;
}

export type UpdateResult = { ok: boolean; error?: string };
/** Successful add returns the face-keyed payload (not column-keyed)
 *  so the in-memory append step in card-modals.ts can spread it into
 *  the card's faces without another translation. */
export type AddResult = {
  ok: boolean;
  error?: string;
  card?: Record<string, string> & { tags?: string; level?: string };
};
export type SuggestResult =
  | { ok: true; translations: Record<string, string>; tags: string; level: string }
  | { ok: false; error?: string; unsupported?: boolean };

/** Build `keyColumn` + `key` for an update/flag request. Walks the
 *  deck's faces in order and picks the first non-empty one. Bilingual
 *  decks resolve to a Lang-coded column; mono decks resolve to the
 *  locale-suffixed column that actually exists in the sheet. */
function pickKey(
  card: Card,
  list: CardList,
  locale: Locale,
): { keyColumn: string; key: string } {
  for (const face of list.faces) {
    const v = card.faces[face.key];
    if (v) return { keyColumn: faceColumn(face, locale), key: v };
  }
  return { keyColumn: faceColumn(list.faces[0], locale), key: card.id };
}

export function createCardApi(deps: CardApiDeps) {
  async function pushCardUpdate(
    card: Card,
    newTexts: Record<string, string>,
    reason: string,
  ): Promise<UpdateResult> {
    const list = deps.getList();
    const locale = deps.getLocale();
    const { keyColumn, key } = pickKey(card, list, locale);
    // Translate face-keyed inputs into column names: keep the original
    // face.key as the cache update key (card.faces[face.key]) but send
    // the deck's column name to the sheet.
    const colByFaceKey = new Map<string, string>();
    const fields: Record<string, string> = {};
    for (const face of list.faces) {
      const v = newTexts[face.key];
      if (v !== undefined && v !== (card.faces[face.key] || "")) {
        const col = faceColumn(face, locale);
        fields[col] = v;
        colByFaceKey.set(face.key, col);
      }
    }
    if (!Object.keys(fields).length && !reason) {
      return { ok: false, error: "nothing to update" };
    }
    // Field changes: always push update.
    if (Object.keys(fields).length) {
      if (!deps.enabled()) return { ok: false, error: "webhook not configured" };
      try {
        const res = await fetch(deps.getWebhookUrl(), {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            action: "update",
            secret: deps.getWebhookSecret(),
            list_id: deps.getListId(),
            keyColumn,
            updates: [{ key, fields }],
          }),
        });
        const data = await res.json();
        if (!data.ok) return { ok: false, error: data.error || "update failed" };
        // Apply locally so the next render reflects the fix. Walk the
        // deck's faces so the in-memory card stays keyed by face.key
        // (what every runtime reader uses), not by column name.
        for (const face of list.faces) {
          if (!colByFaceKey.has(face.key)) continue;
          const v = newTexts[face.key];
          if (v !== undefined) card.faces[face.key] = v;
        }
      } catch (err) {
        return { ok: false, error: String((err as Error)?.message ?? err) };
      }
    }
    // Optional flag log with a reason note.
    if (reason && deps.enabled()) {
      try {
        await fetch(deps.getWebhookUrl(), {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            action: "flag",
            secret: deps.getWebhookSecret(),
            list_id: deps.getListId(),
            user_id: deps.getUserName() || "anonymous",
            key,
            reason,
            current_value: list.faces
              .filter((f) => card.faces[f.key])
              .map((f) => faceColumn(f, locale) + ":" + card.faces[f.key])
              .join(" | "),
          }),
        });
      } catch {
        /* non-fatal */
      }
    }
    return { ok: true };
  }

  async function pushCardAdd(
    texts: Record<string, string>,
    tags: string,
    level: string,
  ): Promise<AddResult> {
    if (!deps.enabled()) return { ok: false, error: deps.tr("add_card_no_sync") };
    const list = deps.getList();
    const locale = deps.getLocale();
    // Drop empties — server enforces "at least 2 non-empty fields".
    // The card payload uses column names (locale-suffixed for mono) so
    // it lands in the right slot on the sheet. Callers pass face-keyed
    // inputs; we translate here.
    const card: Record<string, string> = {};
    for (const face of list.faces) {
      const v = (texts[face.key] || "").trim();
      if (v) card[faceColumn(face, locale)] = v;
    }
    if (Object.keys(card).length < 2) {
      return { ok: false, error: deps.tr("add_card_need_two") };
    }
    if (tags) card.tags = tags;
    if (level) card.level = level;
    // Key column = first face's column; the Apps Script uses it to
    // dedup against existing rows. For mono it's e.g. "country_fr".
    const keyColumn = faceColumn(list.faces[0], locale);
    try {
      const res = await fetch(deps.getWebhookUrl(), {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          action: "add",
          secret: deps.getWebhookSecret(),
          list_id: deps.getListId(),
          keyColumn,
          cards: [card],
        }),
      });
      const data = await res.json();
      if (!data.ok) return { ok: false, error: data.error || "add failed" };
      if (Array.isArray(data.skipped) && data.skipped.length) {
        const reason = data.skipped[0].reason || "skipped";
        if (reason.toLowerCase().includes("duplicate")) {
          return { ok: false, error: deps.tr("add_card_dup") };
        }
        return { ok: false, error: reason };
      }
      // The returned card object is keyed by column names; callers of
      // appendLocalCard expect face-keyed input. Translate back.
      const faceKeyed: Record<string, string> & {
        tags?: string;
        level?: string;
      } = {};
      for (const face of list.faces) {
        const col = faceColumn(face, locale);
        const v = card[col];
        if (v) faceKeyed[face.key] = v;
      }
      if (card.tags) faceKeyed.tags = card.tags;
      if (card.level) faceKeyed.level = card.level;
      return { ok: true, card: faceKeyed };
    } catch (err) {
      return { ok: false, error: String((err as Error)?.message ?? err) };
    }
  }

  async function pushCardSuggest(
    sourceLang: Lang,
    sourceText: string,
  ): Promise<SuggestResult> {
    if (!deps.enabled()) return { ok: false, error: deps.tr("add_card_no_sync") };
    // Collect tag palette from the in-memory deck so suggestions stay
    // consistent with what's already there.
    const tagSet = new Set<string>();
    for (const c of deps.getCards()) {
      if (Array.isArray(c.tags)) {
        for (const tg of c.tags) {
          const v = String(tg || "").trim();
          if (v) tagSet.add(v);
        }
      }
    }
    const knownTags = Array.from(tagSet).sort();
    try {
      const res = await fetch(deps.getWebhookUrl(), {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          action: "suggest",
          secret: deps.getWebhookSecret(),
          list_id: deps.getListId(),
          sourceLang,
          sourceText,
          langs: LANGS,
          knownTags,
        }),
      });
      const data = await res.json();
      if (!data.ok) {
        const err = String(data.error || "");
        if (/unknown action|invalid action/i.test(err)) {
          return { ok: false, unsupported: true };
        }
        return { ok: false, error: err || "suggest failed" };
      }
      return {
        ok: true,
        translations: data.translations || {},
        tags: data.tags || "",
        level: data.level || "",
      };
    } catch (err) {
      return { ok: false, error: String((err as Error)?.message ?? err) };
    }
  }

  return { pushCardUpdate, pushCardAdd, pushCardSuggest };
}
